import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, RotateCcw, XCircle, ExternalLink, Loader2, AlertTriangle, CheckCircle2, Film, Crown } from "lucide-react";
import api from "@/lib/api";

const fmtAge = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const fmtEta = (s) => (s == null ? "—" : s <= 0 ? "finishing" : `~${fmtAge(s)}`);

function StatusPill({ row }) {
  if (row.comp_status === "processing" && row.stuck) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[hsl(var(--destructive))]/12 text-[hsl(var(--destructive))]"><AlertTriangle size={11} /> Stuck</span>;
  }
  if (row.comp_status === "processing") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))]"><Loader2 size={11} className="animate-spin" /> Processing</span>;
  }
  if (row.comp_status === "failed") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[hsl(var(--destructive))]/12 text-[hsl(var(--destructive))]"><XCircle size={11} /> Failed</span>;
  }
  if (row.comp_status === "ready") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/12 text-emerald-600"><CheckCircle2 size={11} /> Ready</span>;
  }
  return <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{row.comp_status}</span>;
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] font-semibold">{label}</div>
      <div className={`mt-1 text-2xl font-display font-semibold tabular-nums ${tone || ""}`}>{value}</div>
    </div>
  );
}

export default function AdminQueue() {
  const [data, setData] = useState({ active: [], recent: [], counts: { processing: 0, failed: 0, stuck: 0 } });
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/admin/compile-queue");
      setData(r.data || { active: [], recent: [], counts: {} });
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (live) {
      pollRef.current = setInterval(() => load(true), 3000);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) clearInterval(pollRef.current);
  }, [live, load]);

  const retry = async (row) => {
    if (!window.confirm(`Retry the compilation for "${row.honoree_name}"? This re-runs the job with its last settings.`)) return;
    setBusyId(row.event_id);
    try {
      await api.post(`/admin/compile-queue/${row.event_id}/retry`);
      toast.success("Compilation re-queued");
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally { setBusyId(null); }
  };

  const cancel = async (row) => {
    if (!window.confirm(`Cancel the in-progress compilation for "${row.honoree_name}"? The event returns to collecting.`)) return;
    setBusyId(row.event_id);
    try {
      await api.post(`/admin/compile-queue/${row.event_id}/cancel`);
      toast.success("Compilation cancelled");
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusyId(null); }
  };

  const rowActions = (row) => (
    <div className="inline-flex gap-1">
      <a href={`/events/${row.event_id}`} target="_blank" rel="noopener noreferrer" className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]" aria-label="Open event"><ExternalLink size={14} /></a>
      {(row.comp_status === "failed" || row.stuck) && (
        <button data-testid={`queue-retry-${row.event_id}`} disabled={busyId === row.event_id} onClick={() => retry(row)}
          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-semibold bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/20 disabled:opacity-50">
          {busyId === row.event_id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Retry
        </button>
      )}
      {row.comp_status === "processing" && (
        <button data-testid={`queue-cancel-${row.event_id}`} disabled={busyId === row.event_id} onClick={() => cancel(row)}
          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-semibold text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 disabled:opacity-50">
          {busyId === row.event_id ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />} Cancel
        </button>
      )}
    </div>
  );

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="admin-queue">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Film size={20} className="text-[hsl(var(--accent))]" /> Compilation queue</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Watch tribute films render in real time and retry any that get stuck.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] cursor-pointer select-none">
            <input type="checkbox" data-testid="queue-live-toggle" checked={live} onChange={(e) => setLive(e.target.checked)} className="accent-[hsl(var(--accent))]" />
            Live (3s)
          </label>
          <button data-testid="queue-refresh" onClick={() => load()} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[hsl(var(--border))] text-sm hover:bg-[hsl(var(--muted))]">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3 max-w-md" data-testid="queue-stats">
        <Stat label="Processing" value={data.counts?.processing ?? 0} tone="text-[hsl(var(--accent))]" />
        <Stat label="Failed" value={data.counts?.failed ?? 0} tone={data.counts?.failed ? "text-[hsl(var(--destructive))]" : ""} />
        <Stat label="Stuck" value={data.counts?.stuck ?? 0} tone={data.counts?.stuck ? "text-[hsl(var(--destructive))]" : ""} />
      </div>

      {/* Active queue */}
      <h2 className="mt-8 font-display text-sm font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Active & failed</h2>
      <div className="mt-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))] text-left text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            <tr>
              <th className="px-4 py-2 font-semibold">Honoree</th>
              <th className="px-4 py-2 font-semibold">Status</th>
              <th className="px-4 py-2 font-semibold min-w-[180px]">Progress</th>
              <th className="px-4 py-2 font-semibold">Age / ETA</th>
              <th className="px-4 py-2 font-semibold">Owner</th>
              <th className="px-4 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">Loading…</td></tr>
            : data.active.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No active or failed jobs. Everything's calm. 🕊️</td></tr>
            : data.active.map((row) => (
              <tr key={row.event_id} data-testid={`queue-row-${row.event_id}`} className="border-t border-[hsl(var(--border))] align-middle">
                <td className="px-4 py-2.5">
                  <div className="font-semibold flex items-center gap-1.5">{row.honoree_name}
                    {row.tier === "legacy" || row.tier === "pro" ? <Crown size={12} className="text-[hsl(var(--accent))]" title={row.tier} /> : null}
                  </div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] capitalize">/{row.slug} · {row.type}</div>
                </td>
                <td className="px-4 py-2.5"><StatusPill row={row} /></td>
                <td className="px-4 py-2.5">
                  {row.comp_status === "processing" ? (
                    <div>
                      <div className="h-1.5 w-full rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                        <div className="h-full rounded-full bg-[hsl(var(--accent))] transition-[width] duration-500" style={{ width: `${Math.max(3, row.progress || 0)}%` }} />
                      </div>
                      <div className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))] truncate">{row.progress || 0}% · {row.stage || "…"}{row.clips_total ? ` (${row.clips_done}/${row.clips_total})` : ""}</div>
                    </div>
                  ) : row.comp_status === "failed" ? (
                    <div className="text-[11px] text-[hsl(var(--destructive))] max-w-[220px] truncate" title={row.error}>{row.error || "Unknown error"}</div>
                  ) : <span className="text-[11px] text-[hsl(var(--muted-foreground))]">—</span>}
                </td>
                <td className="px-4 py-2.5 text-[11px] text-[hsl(var(--muted-foreground))] tabular-nums">
                  <div>age {fmtAge(row.age_seconds)}</div>
                  {row.comp_status === "processing" && <div>eta {fmtEta(row.eta_seconds)}</div>}
                </td>
                <td className="px-4 py-2.5 text-[11px] text-[hsl(var(--muted-foreground))] truncate max-w-[160px]">{row.owner_email}</td>
                <td className="px-4 py-2.5 text-right">{rowActions(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recently completed */}
      <h2 className="mt-8 font-display text-sm font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Recently completed</h2>
      <div className="mt-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))] text-left text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            <tr>
              <th className="px-4 py-2 font-semibold">Honoree</th>
              <th className="px-4 py-2 font-semibold">Clips</th>
              <th className="px-4 py-2 font-semibold">Finished</th>
              <th className="px-4 py-2 font-semibold">Owner</th>
              <th className="px-4 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.recent.length === 0 ? <tr><td colSpan={5} className="px-4 py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">No completed films yet.</td></tr>
            : data.recent.map((row) => (
              <tr key={row.event_id} data-testid={`queue-recent-${row.event_id}`} className="border-t border-[hsl(var(--border))]">
                <td className="px-4 py-2.5"><div className="font-semibold">{row.honoree_name}</div><div className="text-[11px] text-[hsl(var(--muted-foreground))]">/{row.slug}</div></td>
                <td className="px-4 py-2.5 tabular-nums">{row.clips_total || 0}</td>
                <td className="px-4 py-2.5 text-[11px] text-[hsl(var(--muted-foreground))]">{row.finished_at ? new Date(row.finished_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-2.5 text-[11px] text-[hsl(var(--muted-foreground))] truncate max-w-[160px]">{row.owner_email}</td>
                <td className="px-4 py-2.5 text-right">
                  <a href={`/watch/${row.slug}`} target="_blank" rel="noopener noreferrer" className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] inline-grid" aria-label="Watch"><ExternalLink size={14} /></a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
